import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  defs,
  linearGradient,
  stop,
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  primaryColor,
  secondaryColor,
  tooltipContentStyle,
  tooltipCursorStyle,
} from "utils/dashboardUtils";
import { formatLabel, isDesktop } from "utils/utils";
import DashboardFilter from "../DashboardFilter";

const CustomizedAxisTick = ({ x, y, payload, activeFilterType }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill={secondaryColor}
        fontSize={11}
        fontWeight={500}
        transform="rotate(-35)"
      >
        {formatLabel(payload.value, activeFilterType)}
      </text>
    </g>
  );
};

const MembershipGrowthChart = ({
  membershipGrowth,
  activeFilterType,
  className = "",
  showLeadsPageFilter = false,
  dates,
  setDates,
  setActiveFilter,
}) => {
  const { t } = useTranslation();

  const MEMBERSHIP_GROWTH_DATA = membershipGrowth?.map((item) => ({
    month: item.label,
    total: item.newMembers,
    signup: item.newSignups,
  }));

  const getSubtitle = () => {
    switch (activeFilterType) {
      case "DAY":
        return t("Total active members over the last 24 hours");
      case "WEEK":
        return t("Total active members this week");
      case "MONTH":
        return t("Total active members over the last 30 days");
      case "YEAR":
        return t("Total active members over the last 12 months");
      default:
        return t("Total active members");
    }
  };

  const shouldRotate = !isDesktop || MEMBERSHIP_GROWTH_DATA?.length > 6;

  return (
    <div className={`membership_growth dashboard_common_card ${className}`}>
      {/* Title */}
      <div className="d-flex justify-content-between align-items-center mb-5 flex-wrap membership_stat">
        <div>
          <h3 className="fs_20">
            {showLeadsPageFilter ? t("Leads Growth") : t("Membership Growth")}
          </h3>
          <p className="page_sub_title mt-2">{getSubtitle()}</p>
        </div>
        {showLeadsPageFilter && (
          <DashboardFilter
            dates={dates}
            setDates={setDates}
            setActiveFilter={setActiveFilter}
            className={"leads_stats mb-0"}
            selectDateLabel={false}
            selectTypeLabel={false}
          />
        )}
        {/* Legend */}
        <div className="legends">
          <div className="inner_legend">
            <span className="dot total_dot" />
            {t("New Members")}
          </div>
          <div className="inner_legend">
            <span className="dot new_dot" />
            {t("New Signups")}
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={MEMBERSHIP_GROWTH_DATA}
          margin={{ top: 10, right: shouldRotate ? 50 :40, left: shouldRotate ? 50 :40, bottom: 10 }}
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.2} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorSignup" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={secondaryColor} stopOpacity={0.2} />
              <stop offset="95%" stopColor={secondaryColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={isDesktop ? 0 : 1}
            height={shouldRotate ? 60 : 30}
            tick={
              shouldRotate
                ? (props) => (
                    <CustomizedAxisTick
                      {...props}
                      activeFilterType={activeFilterType}
                    />
                  )
                : { fill: secondaryColor, fontSize: 12, fontWeight: 500 }
            }
            tickMargin={shouldRotate ? 0 : 15}
            tickFormatter={
              shouldRotate
                ? undefined
                : (label) => formatLabel(label, activeFilterType)
            }
          />

          <YAxis hide />

          <Tooltip
            cursor={tooltipCursorStyle}
            formatter={(value, name) => [
              value,
              name === "total" ? t("New Members") : t("New Signups"),
            ]}
            labelFormatter={(label) => formatLabel(label, activeFilterType)}
            contentStyle={tooltipContentStyle}
            labelStyle={{ fontWeight: 600 }}
          />

          <CartesianGrid
            vertical={false}
            stroke="#E6EAF0"
            strokeDasharray="0"
          />

          <Area
            type="monotone"
            dataKey="total"
            stroke={primaryColor}
            strokeWidth={2}
            fill="url(#colorTotal)"
            dot={{ r: 4, fill: "#fff", stroke: primaryColor, strokeWidth: 1 }} // ← fill: "#fff"
            activeDot={{ r: 5 }}
          />

          <Area
            type="monotone"
            dataKey="signup"
            stroke={secondaryColor}
            strokeWidth={2}
            fill="url(#colorSignup)"
            dot={false}
            activeDot={{ r: 5 }}
            strokeDasharray="3 3" // ← add this
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MembershipGrowthChart;
